import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer, Box, Button, Grid, Typography, IconButton, CircularProgress,
  Paper, TextField, InputAdornment, Autocomplete
} from '@mui/material';
import { Close, Save, Search, Add, Delete } from '@mui/icons-material';
import DynamicField from '../Dynamic/DynamicField';
import { PostApi, PutApi, GetApi } from "../../Services/api/ApiCall";
import API_URLS from '../../Services/api/action';
import { useUser } from "../../Context/UserContext";

const FarmerForm = ({
  open,
  onClose,
  selectedFarmer,
  actionType,
  onSuccess
}) => {
  // ✅ Updated Initial State: Removed single sapling count, added requested_species list
  const initialFormData = {
    aadhar_no: '',
    farmer_id: '',
    name: '',
    mobile_number: '',
    village: '',
    land_panel_details: '',
    requested_species: [] 
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  
  const [stockDetails, setStockDetails] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  
  // ✅ State for the "Add Species" dropdown selection
  const [selectedSpeciesToAdd, setSelectedSpeciesToAdd] = useState(null);
  
  const { token } = useUser();

  const customColors = {
    primary: '#000000',
    success: '#005500',
    warning: '#ff9933',
    white: '#ffffff',
    black: '#000000'
  };

  const geocodeVillageLand = useCallback(async (village, landDetails) => {
    if (!village || !landDetails) return null;
    
    setGeoLoading(true);
    try {
      const cleanVillage = village.trim();
      const cleanLand = landDetails.replace(/Plot.*|acres?|cent.*|ha.*$/i, '').trim();
      //const cleanLand = landDetails.replace(/Plot.*|acres?|cent.*|ha.*$/i, '').trim();
      // const cleanLand = landDetails.replace(/Plot.*|acres?|cent.*|ha.*$/i, '').trim(); // Removed unused variable
      const fullAddress = `${cleanVillage}, Tamil Nadu, India`.trim();
      
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=3&countrycodes=IN&addressdetails=1`
      );
      const data = await response.json();
      
      if (data && data[0]) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        };
      }
      return { lat: 11.1271, lng: 78.6569 }; // Fallback
    } catch (error) {
      console.error('Geocoding ERROR:', error);
      return { lat: 11.1271, lng: 78.6569 };
    } finally {
      setGeoLoading(false);
    }
  }, []);

  const fetchStockWithGeo = useCallback(async (lat = null, lng = null) => {
    setStockLoading(true);
    try {
      let url = `${API_URLS.species}?page=1`;
      if (lat && lng) {
        url += `&lat=${lat.toFixed(6)}&lng=${lng.toFixed(6)}`;
      }
      
      const response = await GetApi(url, token);
      setStockDetails(response.results || []);
    } catch (error) {
      console.error('Stock fetch failed:', error);
    } finally {
      setStockLoading(false);
    }
  }, [token]);

  // Reset form on open/edit
  useEffect(() => {
    if (open) {
      setSelectedSpeciesToAdd(null); // Reset add dropdown
      
      if (actionType === 'edit' && selectedFarmer) {
        // Logic to map existing data if editing
        // Assuming selectedFarmer has requested_species structure
        setFormData({
          aadhar_no: selectedFarmer.aadhar_no || '',
          farmer_id: selectedFarmer.farmer_id || '',
          name: selectedFarmer.name || '',
          mobile_number: selectedFarmer.mobile_number || '',
          village: selectedFarmer.village || '',
          land_panel_details: selectedFarmer.land_panel_details || '',
          requested_species: selectedFarmer.requested_species || []
        });
        setIsVerified(true);
        fetchStockWithGeo();
      } else {
        setFormData(initialFormData);
        setErrors({});
        setIsVerified(false);
        setStockDetails([]);
      }
    }
  }, [open, actionType, selectedFarmer, fetchStockWithGeo]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  const validateAadhaar = (aadhar) => {
    const aadharRegex = /^\d{4}[\s-]?\d{4}[\s-]?\d{4}$/;
    return aadharRegex.test(aadhar);
  };

  const handleVerifyAadhar = async () => {
    const aadhar = formData.aadhar_no.trim();

    if (!aadhar) {
      setErrors({ aadhar_no: 'Aadhaar number is required' });
      return;
    }

    if (!validateAadhaar(aadhar)) {
      setErrors({ aadhar_no: 'Invalid Aadhaar format (e.g., 1234-5678-9012)' });
      return;
    }

    setVerifyLoading(true);
    setErrors({});

    try {
      const response = await GetApi(`${API_URLS.FarmerAadhar}?aadhar_no=${aadhar}`, token);

      if (response) {
        const village = response.village || '';
        const landDetails = response.land_panel_details || '';
        
        setFormData(prev => ({
          ...prev,
          farmer_id: response.farmer_id || '',
          name: response.name || '',
          mobile_number: response.mobile_number || '',
          village,
          land_panel_details: landDetails,
          requested_species: []
        }));
        setIsVerified(true);

        if (village && landDetails) {
          const geoData = await geocodeVillageLand(village, landDetails);
          if (geoData) {
            await fetchStockWithGeo(geoData.lat, geoData.lng);
          } else {
            await fetchStockWithGeo();
          }
        }
      } else {
        setErrors({ aadhar_no: 'Farmer not found or invalid response' });
      }
    } catch (error) {
      console.error('Verification failed:', error);
      setErrors({ aadhar_no: 'Failed to verify Aadhaar. Please try again.' });
    } finally {
      setVerifyLoading(false);
    }
  };

  // ✅ ADD SPECIES HANDLER
  const handleAddSpecies = () => {
    if (!selectedSpeciesToAdd) return;
    
    // Prevent duplicates
    const exists = formData.requested_species.find(item => item.id === selectedSpeciesToAdd.id);
    if (exists) {
      alert("This species is already added to the list.");
      return;
    }

    setFormData(prev => ({
      ...prev,
      requested_species: [
        ...prev.requested_species,
        {
          id: selectedSpeciesToAdd.id,
          species_name: selectedSpeciesToAdd.species_name,
          production_center: selectedSpeciesToAdd.production_center,
          production_center_address: selectedSpeciesToAdd.production_center_address,
          saplings_available: selectedSpeciesToAdd.saplings_available,
          saplings_requested: '' 
        }
      ]
    }));
    
    setSelectedSpeciesToAdd(null); // Reset dropdown
    setErrors(prev => ({ ...prev, requested_species: '' }));
  };

  // ✅ REMOVE SPECIES HANDLER
  const handleRemoveSpecies = (id) => {
    setFormData(prev => ({
      ...prev,
      requested_species: prev.requested_species.filter(item => item.id !== id)
    }));
  };

  // ✅ UPDATE SAPLINGS COUNT HANDLER
  const handleSaplingCountChange = (id, value) => {
    setFormData(prev => ({
      ...prev,
      requested_species: prev.requested_species.map(item =>
        item.id === id ? { ...item, saplings_requested: value } : item
      )
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.aadhar_no?.trim()) newErrors.aadhar_no = 'Aadhaar number is required';
    
    if (formData.requested_species.length === 0) {
      newErrors.requested_species = 'Please add at least one species';
    } else {
      const invalidItem = formData.requested_species.find(item => !item.saplings_requested || parseInt(item.saplings_requested) <= 0);
      if (invalidItem) {
        newErrors.requested_species = 'Please enter valid sapling counts for all items';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (actionType !== 'edit' && !isVerified) {
      setErrors({ aadhar_no: 'Please verify Aadhaar first' });
      return;
    }

    if (!validateForm()) return;

    setLoading(true);

    // ✅ FORMAT PAYLOAD FOR LIST
    const payloadSpecies = formData.requested_species.map(item => ({
      species_id: item.id, 
      saplings_requested: parseInt(item.saplings_requested),
      production_id : item.production_center,
    }));

    const submitPayload = {
      aadhar_no: formData.aadhar_no,
      farmer_id: formData.farmer_id,
      name: formData.name,
      mobile_number: formData.mobile_number,
      village: formData.village,
      land_panel_details: formData.land_panel_details,
      requested_species: payloadSpecies
    };

    try {
      if (actionType === 'edit') {
        await PutApi(API_URLS.Farmers, { id: selectedFarmer.id }, submitPayload, token);
      } else {
        await PostApi(submitPayload, API_URLS.Farmers, token);
      }
      onSuccess();
    } catch (error) {
      console.error('Submit failed:', error);
      alert('Failed to save farmer data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const autoFilledFields = [
    { name: 'farmer_id', label: 'Farmer ID', xs: 6 },
    { name: 'name', label: 'Farmer Name', xs: 6 },
    { name: 'mobile_number', label: 'Mobile Number', xs: 6 },
    { name: 'village', label: 'Village', xs: 6 },
    { name: 'land_panel_details', label: 'Land Panel Details', multiline: true, rows: 3, xs: 12 },
  ];

  // Filter out already selected species from dropdown options
  const availableStockOptions = stockDetails.filter(
    stock => !formData.requested_species.some(selected => selected.id === stock.id)
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { 
          width: { xs: '100%', md: 700 },
          p: 3,
          bgcolor: customColors.white,
          height: '100vh',
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} sx={{ borderBottom: `2px solid ${customColors.primary}`, pb: 2 }}>
        <Typography variant="h4" sx={{ color: customColors.black, fontWeight: 700 }}>
          {actionType === 'edit' ? 'Edit Farmer' : 'New Request'}
        </Typography>
        <IconButton 
          onClick={onClose} 
          disabled={loading}
          sx={{ color: customColors.warning }}
        >
          <Close fontSize="large" />
        </IconButton>
      </Box>

      {/* Scrollable Form Content */}
      <Box sx={{ height: 'calc(100% - 120px)', overflowY: 'auto', pr: 2 }}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            
            {/* STEP 1: Aadhaar Verification */}
            {(!isVerified && actionType !== 'edit') ? (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid #eee' }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2, color: customColors.black }}>
                    Step 1: Verify Aadhaar
                  </Typography>
                  
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        label="Enter Aadhaar Number"
                        name="aadhar_no"
                        value={formData.aadhar_no}
                        onChange={handleChange}
                        error={!!errors.aadhar_no}
                        helperText={errors.aadhar_no || "Format: 1234-5678-9012"}
                        disabled={verifyLoading}
                        placeholder="1234-5678-9012"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">🪪</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        startIcon={verifyLoading ? <CircularProgress size={20} color="inherit" /> : <Search />}
                        onClick={handleVerifyAadhar}
                        disabled={verifyLoading}
                        sx={{
                          height: '56px',
                          bgcolor: customColors.success,
                          color: customColors.white,
                          fontWeight: 700,
                          '&:hover': { bgcolor: customColors.success }
                        }}
                      >
                        {verifyLoading ? 'Verifying...' : 'Verify'}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ) : (
              <>
                {/* Aadhaar Display */}
                <Grid item xs={12}>
                  <DynamicField
                    field={{ name: 'aadhar_no', label: 'Aadhaar Number' }}
                    value={formData.aadhar_no}
                    loading={loading}
                    readOnly={actionType !== 'edit'} 
                  />
                </Grid>

                {/* Auto-filled Farmer Details */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: customColors.success, mb: 1 }}>
                    ✅ Farmer Details (Auto-filled)
                  </Typography>
                </Grid>
                
                {autoFilledFields.map(field => (
                  <Grid item xs={12} md={field.xs === 12 ? 12 : 6} key={field.name}>
                    <DynamicField
                      field={field}
                      value={formData[field.name]}
                      onChange={handleChange}
                      loading={loading}
                      disabled={true}
                    />
                  </Grid>
                ))}

                {/* Request Details */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: customColors.primary, mb: 1 }}>
                    📝 Request Details
                  </Typography>
                </Grid>

                {/* ✅ ADD NEW SPECIES SECTION */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, borderColor: customColors.primary, bgcolor: '#fff8e1', borderRadius: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={9}>
                        <Autocomplete
                          options={availableStockOptions}
                          value={selectedSpeciesToAdd}
                          onChange={(e, val) => setSelectedSpeciesToAdd(val)}
                          loading={stockLoading || geoLoading}
                          size="small"
                          getOptionLabel={(opt) => opt.species_name || ''}
                          sx={{ width: '100%' }}
                          componentsProps={{ 
                            paper: { 
                              sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } 
                            } 
                          }}
                          noOptionsText={stockLoading ? "Loading..." : "No species available"}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select Species to Add"
                              sx ={{
                                width:"300px"
                              }}
                              placeholder="Search by name..."
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {(stockLoading || geoLoading) ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          // ✅ Custom Dropdown Option showing Address
                          renderOption={(props, option) => {
                            const { key, ...otherProps } = props;
                            return (
                              <li key={key} {...otherProps} style={{ padding: '10px 12px' }}>
                                <Box sx={{ width: '100%' }}>
                                  <Typography variant="body1" fontWeight={600}>
                                    {option.species_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    📍 {option.production_center_address || 'No Address'}
                                  </Typography>
                                  <Typography variant="caption" color="success.main">
                                    📦 Stock: {option.saplings_available}
                                  </Typography>
                                </Box>
                              </li>
                            );
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<Add />}
                          onClick={handleAddSpecies}
                          disabled={!selectedSpeciesToAdd}
                          sx={{ 
                            height: '40px', 
                            bgcolor: customColors.primary, 
                            color: customColors.black, 
                            fontWeight: 700,
                            '&:hover': { bgcolor: customColors.primary }
                          }}
                        >
                          Add
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* ✅ LIST OF ADDED SPECIES (Dynamic Rows) */}
                {formData.requested_species.length > 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                      {formData.requested_species.map((item, index) => (
                        <Paper 
                          key={item.id} 
                          elevation={0} 
                          sx={{ 
                            p: 2, 
                            bgcolor: 'grey.50', 
                            border: '1px solid #e0e0e0', 
                            position: 'relative',
                            borderRadius: 2
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveSpecies(item.id)}
                            sx={{ position: 'absolute', top: 8, right: 8, color: 'error.main' }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                          
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={6}>
                              <Typography variant="body1" fontWeight={700} color={customColors.black}>
                                {index + 1}. {item.species_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                📍 {item.production_center_address}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                📦 Stock Available: {item.saplings_available}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Saplings Requested *"
                                value={item.saplings_requested}
                                onChange={(e) => handleSaplingCountChange(item.id, e.target.value)}
                                inputProps={{ min: 1, max: item.saplings_available }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: 'white'
                                  }
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Box>
                    {errors.requested_species && (
                      <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                        {errors.requested_species}
                      </Typography>
                    )}
                  </Grid>
                )}

              </>
            )}
          </Grid>
        </Box>
      </Box>

      {/* Fixed Bottom Buttons */}
      <Paper 
        elevation={8} 
        sx={{ 
          position: 'absolute', 
          bottom: 24, 
          right: 24, 
          left: 24,
          borderTop: `3px solid ${customColors.primary}`,
          bgcolor: customColors.white
        }}
      >
        <Box display="flex" justifyContent="flex-end" gap={2} p={3}>
          <Button
            variant="outlined"
            startIcon={<Close />}
            onClick={onClose}
            size="large"
            disabled={loading}
            sx={{
              minWidth: 140,
              color: customColors.warning,
              borderColor: customColors.warning,
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          
          {(isVerified || actionType === 'edit') && (
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
              type="submit"
              size="large"
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                minWidth: 160,
                bgcolor: customColors.primary,
                color: customColors.black,
                fontWeight: 700,
                boxShadow: `0 4px 12px ${customColors.primary}30`,
                '&:hover': { bgcolor: customColors.primary }
              }}
            >
              {loading ? 'Saving...' : actionType === 'edit' ? 'Update Farmer' : 'Request Saplings'}
            </Button>
          )}
        </Box>
      </Paper>
    </Drawer>
  );
};

export default FarmerForm;